"use client";

import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth";
import { config } from "@/config";
import { apiGet, type EstadoLive } from "@/lib/api";
import type { FocoBarco } from "@/components/mapa-flota";
import { MapPin } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Leaflet usa `window` -> solo cliente.
const MapaFlota = dynamic(() => import("@/components/mapa-flota"), {
  ssr: false,
  loading: () => <div className="p-6 text-muted-foreground">…</div>,
});

type SortKey = "barco_nombre" | "reserva_activa" | "posicion_ts" | "distancia_puerto" | "consumo_ts";

export default function Home() {
  const t = useTranslations("dashboard");
  const tn = useTranslations("nav");
  const tc = useTranslations("common");
  const { user, loading } = useAuth();
  const router = useRouter();
  const [flota, setFlota] = useState<EstadoLive[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("reserva_activa");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  // Orden de la tabla de flota recordado por navegador (como en CrudTable).
  const SORT_STORAGE_KEY = "flota:sort";
  const [foco, setFoco] = useState<FocoBarco | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  // Botón de la tabla: centra el mapa en el barco (con destello) y lo trae a la vista.
  function verEnMapa(barcoId: string) {
    setFoco((f) => ({ barcoId, nonce: (f?.nonce ?? 0) + 1 }));
    document.getElementById("mapa-flota")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  useEffect(() => {
    if (!user) return;
    apiGet<EstadoLive[]>("/estado-live")
      .then(setFlota)
      .catch((e) => setError(e instanceof Error ? e.message : "Error"));
  }, [user]);

  // Auto-refresh "casi en vivo" (lo que mueve el MQTT) cada 20 s.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      apiGet<EstadoLive[]>("/estado-live").then(setFlota).catch(() => {});
    }, 20000);
    return () => clearInterval(id);
  }, [user]);

  const fechaHora = (v: string | null) =>
    v
      ? new Date(v).toLocaleString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Madrid",
        })
      : "—";

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = flota ?? [];
    if (q) {
      out = out.filter((b) =>
        [b.barco_nombre, b.ubicacion, b.usuario_nombre, b.usuario_email, b.referencia]
          .some((v) => String(v ?? "").toLowerCase().includes(q)),
      );
    }
    const val = (b: EstadoLive): string | number => {
      switch (sortKey) {
        case "barco_nombre": return b.barco_nombre ?? "";
        case "reserva_activa": return b.reserva_activa ? 1 : 0;
        case "posicion_ts": return b.posicion_ts ? new Date(b.posicion_ts).getTime() : 0;
        case "distancia_puerto": return b.distancia_puerto != null ? Number(b.distancia_puerto) : -1;
        case "consumo_ts": return b.consumo_ts ? new Date(b.consumo_ts).getTime() : 0;
      }
    };
    return [...out].sort((a, b) => {
      const av = val(a), bv = val(b);
      let r: number;
      if (typeof av === "number" && typeof bv === "number") r = av - bv;
      else r = String(av).localeCompare(String(bv), "es");
      return sortDir === "asc" ? r : -r;
    });
  }, [flota, search, sortKey, sortDir]);

  // Paginación
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages - 1);
  const pageRows = rows.slice(current * pageSize, current * pageSize + pageSize);

  useEffect(() => {
    setPage(0);
  }, [search, pageSize]);

  // Restaurar el orden guardado al montar.
  useEffect(() => {
    const claves: SortKey[] = ["barco_nombre", "reserva_activa", "posicion_ts", "distancia_puerto", "consumo_ts"];
    try {
      const raw = localStorage.getItem(SORT_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { key: SortKey; dir: "asc" | "desc" };
      if (claves.includes(saved.key)) {
        setSortKey(saved.key);
        setSortDir(saved.dir === "asc" ? "asc" : "desc");
      }
    } catch {
      /* localStorage no disponible o JSON corrupto: se ignora */
    }
  }, [SORT_STORAGE_KEY]);

  function toggleSort(k: SortKey) {
    const nextDir: "asc" | "desc" =
      sortKey === k ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(k);
    setSortDir(nextDir);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ key: k, dir: nextDir }));
    } catch {
      /* localStorage no disponible: se ignora */
    }
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground">
        {tc("cargando")}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-4xl font-semibold tracking-tight">
            {t("bienvenido", { app: config.appName })}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {t("introCta", { tagline: config.tagline, company: config.companyName })}
          </p>
          <Button size="lg" className="mt-6" onClick={() => router.push("/login")}>
            {tn("iniciarSesion")}
          </Button>
        </div>
      </div>
    );
  }

  const totalBarcos = flota?.length ?? 0;
  const totalActivas = flota?.filter((b) => b.reserva_activa).length ?? 0;

  const Th = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(k)}>
      {children}
      {sortKey === k && (sortDir === "asc" ? " ▲" : " ▼")}
    </TableHead>
  );

  return (
    <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6">
      <PageHeader
        title={t("hola", { user: user.username })}
        subtitle={t("panel", { company: config.companyName })}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Tarjetas compactas */}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="rounded-lg border bg-card px-4 py-2">
          <div className="text-xs text-muted-foreground">{t("barcos")}</div>
          <div className="text-2xl font-semibold tabular-nums">{totalBarcos}</div>
        </div>
        <div className="rounded-lg border bg-card px-4 py-2">
          <div className="text-xs text-muted-foreground">{t("reservasActivas")}</div>
          <div className="text-2xl font-semibold tabular-nums">{totalActivas}</div>
        </div>
      </div>

      {/* Mapa de la flota */}
      <div id="mapa-flota" className="mt-6 h-[45vh] w-full isolate overflow-hidden rounded-lg border">
        <MapaFlota foco={foco} />
      </div>

      {/* Tabla de flota */}
      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight">{t("tablaTitulo")}</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{t("autoRefresh")}</span>
            <Input
              placeholder={tc("buscar")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-48"
            />
          </div>
        </div>

        <div className="mt-4 rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <Th k="barco_nombre">{t("colBarco")}</Th>
                <Th k="reserva_activa">{t("colReserva")}</Th>
                <Th k="posicion_ts">{t("posicion")}</Th>
                <Th k="distancia_puerto">{t("distanciaPuerto")}</Th>
                <Th k="consumo_ts">{t("consumo")}</Th>
                <TableHead className="w-px text-right">{t("colMapa")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {flota ? t("sinBarcos") : tc("cargando")}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((b) => (
                  <TableRow key={b.barco_id}>
                    <TableCell>
                      <div className="font-medium">{b.barco_nombre}</div>
                      {b.ubicacion && (
                        <div className="text-xs text-muted-foreground">{b.ubicacion}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.reserva_activa && b.reserva_id ? (
                        // Clic en la reserva activa → detalle del recorrido de esa reserva.
                        <Link
                          href={`/recorrido/${b.reserva_id}`}
                          className="group -mx-1 block rounded-md px-1 py-0.5 transition-colors hover:bg-accent/50"
                        >
                          <Badge tone="emerald" className="group-hover:underline">
                            {t("activa")}
                          </Badge>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {(b.usuario_nombre || b.usuario_email || "—")}
                            {b.referencia ? ` · ${b.referencia}` : ""}
                          </div>
                        </Link>
                      ) : (
                        <Badge tone="neutral">{t("noActiva")}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.lat != null && b.lon != null ? (
                        <div>
                          <div className="tabular-nums">
                            {b.lat.toFixed(5)}, {b.lon.toFixed(5)}
                          </div>
                          <div className="text-xs text-muted-foreground">{fechaHora(b.posicion_ts)}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.distancia_puerto != null ? (
                        <span className="tabular-nums">
                          {Number(b.distancia_puerto).toFixed(2)} {t("mn")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.consumo != null ? (
                        <div>
                          <div className="tabular-nums">{b.consumo}</div>
                          <div className="text-xs text-muted-foreground">{fechaHora(b.consumo_ts)}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={b.lat == null || b.lon == null}
                        onClick={() => verEnMapa(b.barco_id)}
                      >
                        <MapPin className="mr-1 h-4 w-4" />
                        {t("verEnMapa")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Pagination page={current} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />
      </section>
    </div>
  );
}
